import { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useOrg } from '../../../context/OrgContext';
import { useAuth } from '../../../context/useAuth';
import {
  getBillingPlansApi,
  getOrgSubscriptionApi,
  checkoutSubscriptionApi,
  verifySubscriptionPaymentApi,
  cancelSubscriptionApi,
} from '../../../api/apiServices';

const formatNaira = (amount) => `₦${Number(amount || 0).toLocaleString('en-NG')}`;

const UsageBar = ({ label, used, limit }) => {
  const unlimited = limit === null || limit === undefined;
  const pct = unlimited ? 0 : Math.min(100, Math.round((used / Math.max(limit, 1)) * 100));
  const nearLimit = !unlimited && used >= limit;
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1.5">
        <span className="font-semibold text-slate-600">{label}</span>
        <span className={`font-bold ${nearLimit ? 'text-rose-600' : 'text-slate-700'}`}>
          {used} {unlimited ? '' : `/ ${limit}`}
          {unlimited && <span className="text-slate-400 font-medium"> (unlimited)</span>}
        </span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${nearLimit ? 'bg-rose-500' : 'bg-teal-500'}`}
          style={{ width: unlimited ? '18%' : `${pct}%` }}
        />
      </div>
    </div>
  );
};

export default function AdminBillingPage() {
  const { currentOrgId, currentOrg } = useOrg();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [checkoutLoadingPlan, setCheckoutLoadingPlan] = useState(null);
  const [canceling, setCanceling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [notice, setNotice] = useState(null); // { type, message }

  const platformRole = (user?.role || '').toLowerCase();
  const isPlatformAdmin = platformRole === 'admin' || platformRole === 'superadmin';
  const canManageBilling = isPlatformAdmin || ['owner', 'admin'].includes(currentOrg?.myRole);

  const loadSubscription = useCallback(async () => {
    if (!currentOrgId) return;
    try {
      const res = await getOrgSubscriptionApi(currentOrgId);
      if (res.success) setSubscription(res.subscription);
    } catch (err) {
      console.error('Failed to load subscription:', err);
    }
  }, [currentOrgId]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getBillingPlansApi().then((res) => { if (res.success) setPlans(res.plans); }),
      loadSubscription(),
    ]).finally(() => setLoading(false));
  }, [currentOrgId, loadSubscription]);

  // Paystack redirects back here with ?reference=... once checkout finishes.
  useEffect(() => {
    const reference = searchParams.get('reference');
    if (!reference) return;

    setVerifying(true);
    verifySubscriptionPaymentApi(reference)
      .then((res) => {
        if (res.success) {
          setNotice({ type: 'success', message: 'Payment confirmed -- your plan has been updated.' });
          loadSubscription();
        } else {
          setNotice({ type: 'error', message: res.message || 'Payment was not successful.' });
        }
      })
      .catch((err) => {
        setNotice({ type: 'error', message: err.response?.data?.message || 'Could not verify payment.' });
      })
      .finally(() => {
        setVerifying(false);
        // Drop ?reference= from the URL so a page refresh doesn't re-verify.
        setSearchParams({}, { replace: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChoosePlan = async (planId) => {
    if (!currentOrgId) return;
    setNotice(null);
    setCheckoutLoadingPlan(planId);
    try {
      const callbackUrl = `${window.location.origin}/admin/billing`;
      const res = await checkoutSubscriptionApi({ organizationId: currentOrgId, plan: planId, callbackUrl });
      if (res.activatedImmediately) {
        setNotice({ type: 'success', message: 'Your organization is now on the Free plan.' });
        await loadSubscription();
        setCheckoutLoadingPlan(null);
      } else if (res.authorizationUrl) {
        window.location.href = res.authorizationUrl; // hand off to Paystack's hosted checkout
      }
    } catch (err) {
      setNotice({ type: 'error', message: err.response?.data?.message || 'Failed to start checkout.' });
      setCheckoutLoadingPlan(null);
    }
  };

  const handleCancel = async () => {
    setCanceling(true);
    try {
      const res = await cancelSubscriptionApi(currentOrgId);
      if (res.success) {
        setNotice({ type: 'success', message: res.message });
        await loadSubscription();
      }
    } catch (err) {
      setNotice({ type: 'error', message: err.response?.data?.message || 'Failed to cancel subscription.' });
    } finally {
      setCanceling(false);
      setShowCancelConfirm(false);
    }
  };

  if (!currentOrgId) {
    return (
      <div className="max-w-2xl">
        <div className="bg-white border border-dashed border-slate-300 rounded-3xl p-12 text-center">
          <div className="w-14 h-14 mx-auto mb-4 bg-slate-100 rounded-2xl flex items-center justify-center text-2xl text-slate-400">
            <i className="ri-bank-card-2-line" />
          </div>
          <p className="text-slate-700 font-semibold">Billing is per organization</p>
          <p className="text-slate-500 text-sm mt-1.5 mb-6">
            Switch to an organization from the sidebar to view or manage its plan. "Personal" courses aren't billed.
          </p>
          <Link
            to="/admin/organization"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white text-sm font-semibold rounded-xl hover:bg-teal-700"
          >
            <i className="ri-building-2-line" /> Go to Organizations
          </Link>
        </div>
      </div>
    );
  }

  if (loading || verifying) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
        {verifying && <span className="ml-3 text-sm text-slate-500">Verifying payment...</span>}
      </div>
    );
  }

  const currentPlanId = subscription?.plan?.id || 'free';

  return (
    <div className="max-w-5xl">
      {notice && (
        <div className={`mb-6 px-4 py-3 rounded-2xl border flex items-center gap-3 text-sm font-medium ${
          notice.type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-rose-50 border-rose-200 text-rose-700'
        }`}>
          <i className={`ri-${notice.type === 'success' ? 'check-circle' : 'error-warning'}-line text-lg`} />
          {notice.message}
        </div>
      )}

      {/* Current plan + usage */}
      <div className="bg-white border border-slate-200 rounded-3xl p-6 mb-8">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <div>
            <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide">Current Plan</p>
            <h2 className="text-2xl font-bold text-slate-900 mt-0.5">{subscription?.plan?.name || 'Free'}</h2>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${
              subscription?.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'
            }`}>
              {subscription?.status === 'active' ? 'Active' : subscription?.status || 'Active'}
            </span>
            {currentPlanId !== 'free' && canManageBilling && (
              <button
                onClick={() => setShowCancelConfirm(true)}
                className="text-xs font-semibold text-rose-600 hover:bg-rose-50 px-3 py-1.5 rounded-full transition-colors"
              >
                Cancel plan
              </button>
            )}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-6">
          <UsageBar label="Courses" used={subscription?.usage?.courses || 0} limit={subscription?.plan?.limits?.maxCourses} />
          <UsageBar label="Team Members" used={subscription?.usage?.teamMembers || 0} limit={subscription?.plan?.limits?.maxTeamMembers} />
        </div>

        {subscription?.currentPeriodEnd && (
          <p className="text-xs text-slate-400 mt-5">
            Current period ends {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
          </p>
        )}
        {!canManageBilling && (
          <p className="text-xs text-slate-400 mt-5">Only an owner or admin of this organization can change its plan.</p>
        )}
      </div>

      {/* Plan comparison */}
      <div className="grid md:grid-cols-3 gap-5">
        {plans.map((plan) => {
          const isCurrent = plan.id === currentPlanId;
          return (
            <div
              key={plan.id}
              className={`bg-white border rounded-3xl p-6 flex flex-col ${isCurrent ? 'border-teal-500 ring-2 ring-teal-100' : 'border-slate-200'}`}
            >
              <h3 className="text-lg font-bold text-slate-900">{plan.name}</h3>
              <p className="mt-2">
                <span className="text-3xl font-bold text-slate-900">{plan.price === 0 ? 'Free' : formatNaira(plan.price)}</span>
                {plan.price > 0 && <span className="text-slate-400 text-sm">/mo</span>}
              </p>

              <ul className="mt-5 space-y-2.5 flex-1">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-600">
                    <i className="ri-check-line text-teal-600 mt-0.5 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleChoosePlan(plan.id)}
                disabled={isCurrent || !canManageBilling || checkoutLoadingPlan}
                className={`mt-6 w-full py-3 rounded-xl text-sm font-semibold transition-colors disabled:cursor-not-allowed ${
                  isCurrent
                    ? 'bg-teal-50 text-teal-700 cursor-default'
                    : 'bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-50'
                }`}
              >
                {isCurrent ? 'Current Plan' : checkoutLoadingPlan === plan.id ? 'Redirecting...' : plan.price === 0 ? 'Switch to Free' : 'Upgrade'}
              </button>
            </div>
          );
        })}
      </div>

      {showCancelConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-sm w-full">
            <h3 className="text-lg font-bold text-slate-900 mb-2">Cancel your plan?</h3>
            <p className="text-sm text-slate-500 mb-6">
              Your organization will move to the Free plan immediately, and its usage limits will drop accordingly.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelConfirm(false)}
                disabled={canceling}
                className="flex-1 py-2.5 border border-slate-300 rounded-xl font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Keep plan
              </button>
              <button
                onClick={handleCancel}
                disabled={canceling}
                className="flex-1 py-2.5 bg-rose-600 hover:bg-rose-700 disabled:bg-rose-400 text-white rounded-xl font-semibold"
              >
                {canceling ? 'Canceling...' : 'Yes, cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
